import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { authAPI, multiplaiAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, User, Link2, Unlink2, Lock, Loader2, Building2, Check } from "lucide-react";

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [multiplaiStatus, setMultiplaiStatus] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState("");
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await multiplaiAPI.getStatus();
        setMultiplaiStatus(res.data);
        if (res.data.multiplai_org_id) setSelectedOrg(String(res.data.multiplai_org_id));
        if (res.data.user_linked) {
          const orgRes = await multiplaiAPI.getOrganizations();
          setOrgs(orgRes.data.organizations || []);
        }
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    fetchStatus();
  }, []);

  const connectMultiplai = async () => {
    setConnecting(true);
    try {
      const res = await multiplaiAPI.authorize();
      window.location.href = res.data.authorize_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start SSO");
      setConnecting(false);
    }
  };

  const disconnectMultiplai = async () => {
    if (!window.confirm("Disconnect your Multiplai account? You can reconnect later.")) return;
    try {
      await multiplaiAPI.disconnect();
      setMultiplaiStatus((prev) => ({ ...prev, user_linked: false, multiplai_org_id: null }));
      toast.success("Multiplai account disconnected");
    } catch { toast.error("Failed to disconnect"); }
  };

  const saveOrg = async () => {
    if (!selectedOrg) return;
    try {
      await multiplaiAPI.setOrg(selectedOrg);
      toast.success("Organization saved!");
      // Reload orgs
      const orgRes = await multiplaiAPI.getOrganizations();
      setOrgs(orgRes.data.organizations || []);
    } catch { toast.error("Failed to save"); }
  };

  const changePassword = async () => {
    if (password.length < 6) { toast.error("Min 6 characters"); return; }
    if (password !== confirmPassword) { toast.error("Passwords don't match"); return; }
    setSavingPassword(true);
    try {
      await authAPI.setPassword(password);
      setPassword(""); setConfirmPassword("");
      toast.success("Password updated!");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSavingPassword(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="settings-page">
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="h-8 px-2 text-slate-500">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="font-heading font-bold text-base text-slate-900">Account Settings</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-8">
        {/* Profile */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <User className="w-5 h-5 text-indigo-500" />
            <h2 className="font-heading font-semibold text-lg text-slate-900">Profile</h2>
          </div>
          <div className="space-y-2 text-sm text-slate-600">
            <div><span className="text-slate-400 w-20 inline-block">Name:</span> {user?.name}</div>
            <div><span className="text-slate-400 w-20 inline-block">Email:</span> {user?.email}</div>
            <div><span className="text-slate-400 w-20 inline-block">Role:</span> {user?.role}</div>
          </div>
        </section>

        {/* Change Password */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Lock className="w-5 h-5 text-indigo-500" />
            <h2 className="font-heading font-semibold text-lg text-slate-900">Change Password</h2>
          </div>
          <div className="grid grid-cols-2 gap-4 max-w-md">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">New Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" className="h-9 bg-white" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-600">Confirm</Label>
              <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm" className="h-9 bg-white" />
            </div>
          </div>
          <Button onClick={changePassword} disabled={savingPassword || !password} size="sm" className="mt-3 bg-slate-900 hover:bg-slate-800 text-white">
            {savingPassword ? "Saving..." : "Update Password"}
          </Button>
        </section>

        {/* Multiplai.tech Integration */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Link2 className="w-5 h-5 text-indigo-500" />
              <h2 className="font-heading font-semibold text-lg text-slate-900">Multiplai.tech Integration</h2>
            </div>
            {multiplaiStatus?.user_linked && (
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                <Check className="w-3 h-3 mr-1" />Connected
              </Badge>
            )}
          </div>

          <p className="text-sm text-slate-500 mb-4">
            Connect your Multiplai account to import departments and team members directly into the game.
          </p>

          {loading ? (
            <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" /></div>
          ) : multiplaiStatus?.user_linked ? (
            <div className="space-y-4">
              {/* Organization Selector */}
              <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">Select Organization</span>
                </div>
                <p className="text-xs text-slate-400 mb-3">Choose which organization's departments and users to import into game boards.</p>
                <div className="flex items-center gap-2">
                  <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                    <SelectTrigger data-testid="org-select" className="h-9 bg-white flex-1">
                      <SelectValue placeholder="Choose organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {orgs.map((o) => (
                        <SelectItem key={o.id} value={String(o.id)}>{o.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={saveOrg} disabled={!selectedOrg} className="bg-indigo-600 hover:bg-indigo-700 text-white h-9">
                    Save
                  </Button>
                </div>
              </div>

              <Button variant="outline" size="sm" data-testid="disconnect-multiplai-btn" onClick={disconnectMultiplai} className="text-red-600 border-red-200 hover:bg-red-50">
                <Unlink2 className="w-3.5 h-3.5 mr-1.5" />Disconnect Multiplai
              </Button>
            </div>
          ) : (
            <Button data-testid="connect-multiplai-btn" onClick={connectMultiplai} disabled={connecting} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
              Connect Multiplai Account
            </Button>
          )}
        </section>
      </main>
    </div>
  );
}
